type DateFormat = 'short' | 'long';

const DATE_OPTIONS: Record<DateFormat, Intl.DateTimeFormatOptions> = {
    short: { month: 'short', day: 'numeric', year: 'numeric' },
    long: { month: 'long', day: 'numeric', year: 'numeric' },
};

const TIME_OPTIONS: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' };

// A bare `YYYY-MM-DD` (a LocalDate, e.g. `task.taskDate`) must be parsed in LOCAL parts:
// `new Date('2026-06-09')` is midnight UTC and renders as the previous day for any admin west of
// UTC. Timestamps (with a time/zone) keep the normal instant parse.
function toDate(date: string | Date): Date {
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
        const [year, month, day] = date.split('-').map(Number) as [number, number, number];
        return new Date(year, month - 1, day);
    }
    return new Date(date);
}

export function formatDate(date: string | Date, format: DateFormat = 'short'): string {
    return toDate(date).toLocaleDateString(undefined, DATE_OPTIONS[format]);
}

export function formatDateTime(date: string | Date, format: DateFormat = 'short'): string {
    const d = toDate(date);
    return `${d.toLocaleDateString(undefined, DATE_OPTIONS[format])} at ${d.toLocaleTimeString(undefined, TIME_OPTIONS)}`;
}

const RELATIVE_UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['year', 365 * 24 * 60 * 60 * 1000],
    ['month', 30 * 24 * 60 * 60 * 1000],
    ['week', 7 * 24 * 60 * 60 * 1000],
    ['day', 24 * 60 * 60 * 1000],
    ['hour', 60 * 60 * 1000],
    ['minute', 60 * 1000],
];

const relativeFormatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

/**
 * "3 weeks ago" / "in 2 days" / "yesterday". Picks the largest unit that fits.
 */
export function formatRelative(date: string | Date | undefined): string {
    if (!date) return '—';
    const ms = new Date(date).getTime() - Date.now();
    const abs = Math.abs(ms);
    for (const [unit, size] of RELATIVE_UNITS) {
        if (abs >= size) return relativeFormatter.format(Math.round(ms / size), unit);
    }
    return relativeFormatter.format(Math.round(ms / 1000), 'second');
}
